import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { 
  collection, getDocs, addDoc, updateDoc, deleteDoc, 
  doc, query, orderBy, serverTimestamp 
} from 'firebase/firestore';

const MEMO_COLLECTION = 'E2B_DEV_MEMOS';

// 날짜 유틸 함수
const getLocalDateString = (dateObj = new Date()) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DevMemo = () => {
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const initialForm = { 
    id: null, 
    releaseDate: getLocalDateString(), 
    version: '', 
    type: 'UPDATE', // UPDATE, PATCH, FIX
    title: '', 
    content: '',
    author: '관리자',
    attachments: []
  };
  const [formData, setFormData] = useState(initialForm);

  // --- [데이터 불러오기] ---
  const fetchMemos = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, MEMO_COLLECTION), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => {
        const item = doc.data();
        let displayDate = item.releaseDate || (item.createdAt ? (typeof item.createdAt.toDate === 'function' ? getLocalDateString(item.createdAt.toDate()) : item.createdAt.toString().substring(0, 10)) : getLocalDateString());

        return { 
          ...item, 
          id: doc.id,
          releaseDate: displayDate,
          version: item.version || 'v1.0.0',
          type: item.type || 'UPDATE',
          title: item.title || '제목 없음',
          content: item.content || ''
        };
      });

      // 자바스크립트 버전 최신순 정렬 보장
      data.sort((a, b) => {
        return b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' });
      });

      setMemos(data);
    } catch (e) {
      console.error("로드 실패:", e);
      const snap = await getDocs(collection(db, MEMO_COLLECTION));
      const fallbackData = snap.docs.map(d => ({ ...d.data(), id: d.id, releaseDate: getLocalDateString(), version: 'v1.0.0', type: 'UPDATE' }));
      setMemos(fallbackData);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMemos(); }, []);

  // --- [저장 / 수정 / 삭제] ---
  const onSave = async (e) => {
    e.preventDefault();
    if (!formData.releaseDate || !formData.version || !formData.title || !formData.content) {
      return alert("필수 항목(*)을 모두 입력해주세요.");
    }
    
    setLoading(true);
    try {
      const { id, ...saveData } = formData;

      if (id) {
        await updateDoc(doc(db, MEMO_COLLECTION, id), { 
          ...saveData, 
          updatedAt: serverTimestamp() 
        });
        alert("✅ 업데이트 명세가 성공적으로 수정되었습니다.");
      } else {
        await addDoc(collection(db, MEMO_COLLECTION), { 
          ...saveData, 
          createdAt: serverTimestamp() 
        });
        alert("✅ 신규 업데이트 로그가 성공적으로 배포 등록되었습니다.");
      }
      setFormData(initialForm);
      await fetchMemos();
    } catch (e) { 
      console.error(e);
      alert("서버 오류로 저장에 실패했습니다."); 
    }
    setLoading(false);
  };

  const handleEditStart = (memo) => {
    setFormData(memo);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm("정말 이 배포 로그 명세를 제거하시겠습니까?")) { 
      try {
        await deleteDoc(doc(db, MEMO_COLLECTION, id)); 
        alert("제거 완료되었습니다.");
        await fetchMemos();
        setFormData(initialForm);
      } catch (e) {
        alert("삭제 중 오류가 발생했습니다.");
      }
    }
  };

  const getBadgeStyle = (type) => {
    const base = { padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '900', color: 'white', marginRight: '8px' };
    if (type === 'PATCH') return { ...base, background: '#3b82f6' }; 
    if (type === 'FIX') return { ...base, background: '#f97316' };  
    return { ...base, background: '#6366f1' };                      
  };

  // 🚀 [Full HD 100% 와이드 스펙 최적화 스타일 개편]
  const styles = {
    // 내부 중복 패딩을 완전히 0으로 만들어 부모 영역의 최대 가로 폭을 그대로 활용합니다.
    wrapper: { width: '100%', boxSizing: 'border-box', padding: '0px', margin: '0px' },
    
    // 이중 카드 구조를 탈피하여 좌/우 컴포넌트가 부모 창 안에서 직접 배치되는 플렉스 구조
    container: { display: 'flex', gap: '25px', alignItems: 'flex-start', width: '100%', boxSizing: 'border-box' },
    
    // 좌측 입력 폼 박스: 한 눈에 들어오는 전용 규격 카드로 재배치
    leftFormBox: { background: 'white', padding: '25px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', width: '350px', boxSizing: 'border-box', flexShrink: 0 },
    
    // 우측 목록 상자: 가로 공간을 제한 없이 100% 확장하여 스크린 끝까지 와이드하게 채움
    rightListBox: { flex: 1, boxSizing: 'border-box', minWidth: 0 },
    
    sectionTitle: { fontSize: '16px', fontWeight: '800', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' },
    label: { fontSize: '12px', fontWeight: 'bold', color: '#475569', marginBottom: '6px', display: 'block' },
    input: { width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', boxSizing: 'border-box', marginBottom: '15px', fontSize: '13px', outline: 'none' },
    textarea: { width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', boxSizing: 'border-box', marginBottom: '15px', fontSize: '13px', outline: 'none', height: '220px', resize: 'none' },
    
    btnSubmit: { width: '100%', padding: '14px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' },
    btnCancel: { width: '100%', padding: '10px', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', marginTop: '8px' },
    
    historyCard: { background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '15px', display: 'flex', gap: '18px', width: '100%', boxSizing: 'border-box', boxShadow: '0 4px 15px rgba(0,0,0,0.01)' },
    leftIndicator: (type) => ({
      width: '4px', borderRadius: '4px', flexShrink: 0,
      background: type === 'PATCH' ? '#3b82f6' : type === 'FIX' ? '#f97316' : '#6366f1'
    }),
    historyMain: { flex: 1, minWidth: 0 },
    historyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    historyTitle: { fontSize: '16px', fontWeight: '800', color: '#0f172a', marginTop: '4px' },
    historyContent: { fontSize: '13px', color: '#475569', lineHeight: '1.7', marginTop: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' },
    
    actionBtn: (color) => ({ background: 'none', border: 'none', color: color, fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', marginLeft: '12px', padding: 0 })
  };

  return (
    <div style={styles.wrapper}>
      {/* 🚀 중복되던 내부 상단 타이틀 레이아웃 코드를 완벽히 걷어내어 부모 타이틀만 깔끔하게 남겨두었습니다. */}
      
      <div style={styles.container}>
        
        {/* 📋 [좌측 레일] 시스템 업데이트 내역 등록 폼 (화이트 카드 독립화) */}
        <div style={styles.leftFormBox}>
          <div style={styles.sectionTitle}>
            <span style={{ color: '#10b981', fontWeight: '900' }}>|</span> 시스템 업데이트 내역 등록
          </div>
          <form onSubmit={onSave}>
            <label style={styles.label}>배포 일자 *</label>
            <input type="date" style={styles.input} value={formData.releaseDate} onChange={e=>setFormData({...formData, releaseDate: e.target.value})} />
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={styles.label}>리비전 버전 *</label>
                <input style={styles.input} placeholder="예: v1.3.0" value={formData.version} onChange={e=>setFormData({...formData, version: e.target.value})} />
              </div>
              <div>
                <label style={styles.label}>패치 유형 *</label>
                <select style={styles.input} value={formData.type} onChange={e=>setFormData({...formData, type: e.target.value})}>
                  <option value="UPDATE">UPDATE (기능추가)</option>
                  <option value="PATCH">PATCH (패치)</option>
                  <option value="FIX">FIX (버그수정)</option>
                </select>
              </div>
            </div>

            <label style={styles.label}>업데이트 한줄 요약 *</label>
            <input style={styles.input} placeholder="예: 차량 대장 모듈 NoSQL 구조 고도화" value={formData.title} onChange={e=>setFormData({...formData, title: e.target.value})} />

            <label style={styles.label}>상세 변경 명세 내역 *</label>
            <textarea style={styles.textarea} placeholder="변경 메타 정보를 기술하세요." value={formData.content} onChange={e=>setFormData({...formData, content: e.target.value})} />

            <button type="submit" style={styles.btnSubmit}>
              💾 {formData.id ? '로그 파일 수정 실행' : '로그 파일 배포 실행'}
            </button>
            
            {formData.id && (
              <button type="button" style={styles.btnCancel} onClick={() => setFormData(initialForm)}>
                수정 취소
              </button>
            )}
          </form>
        </div>

        {/* 📋 [우측 레일] 배포 가드 업데이트 명세 이력 목록 (부모 넓이를 100% 점유하여 대시보드 꽉 채움) */}
        <div style={styles.rightListBox}>
          <div style={{...styles.sectionTitle, marginBottom: '25px'}}>
            📋 E2B-CRM 커밋 패키지 및 배포 가드 업데이트 명세 이력
          </div>
          
          <div style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', paddingRight: '5px' }}>
            {memos.length > 0 ? (
              memos.map(memo => (
                <div key={memo.id} style={styles.historyCard}>
                  {/* 패치 유형별 왼쪽 컬러 선 인디케이터 */}
                  <div style={styles.leftIndicator(memo.type)} />
                  
                  {/* 데이터 필드 바인딩 */}
                  <div style={styles.historyMain}>
                    <div style={styles.historyHeader}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={getBadgeStyle(memo.type)}>{memo.type}</span>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#475569' }}>{memo.version}</span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>📅 {memo.releaseDate}</span>
                        <button onClick={() => handleEditStart(memo)} style={styles.actionBtn('#3b82f6')}>수정</button>
                        <button onClick={() => handleDelete(memo.id)} style={styles.actionBtn('#94a3b8')}>제거</button>
                      </div>
                    </div>
                    
                    <div style={styles.historyTitle}>{memo.title}</div>
                    <div style={styles.historyContent}>{memo.content}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '13px' }}>
                등록된 시스템 업데이트 명세 이력이 존재하지 않습니다.
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default DevMemo;